import emptySliceApi from '../../shared/store/emptyApiSlice';
import {
  AccountSelectDto,
  AccountSelectQueryResult,
  AdminInvite,
} from './models';

interface AccountListApiRequest {
  page?: number;
  pageSize?: number;
  accountId?: string;
  search?: string;
  sort?: string | null;
  token?: string;
  channelEngagement?: boolean;
  orgEngagement?: boolean;
  atpIlt?: boolean;
  pointsOfContact?:
    | {
        firstName: string;
        lastName: string;
      }[]
    | string[];
  engagements?: string[];
}

interface Contact {
  firstName: 'string';
  lastName: 'string';
}

export const apiSlice = emptySliceApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccountList: builder.query<
      AccountSelectQueryResult,
      AccountListApiRequest
    >({
      query: ({
        accountId = '',
        page = '',
        pageSize = 10,
        search = '',
        sort = 'null',
        token = null,
        channelEngagement = false,
        orgEngagement = false,
        atpIlt = false,
      }) => ({
        headers: {
          ...(token && { authorization: `Bearer ${token}` }),
        },
        url: `v4/accounts/filteredList?accountId=${accountId}&page=${page}&pageSize=${pageSize}&search=${search}&sort=${sort}${
          channelEngagement ? '&ChannelEngagement=true' : ''
        }${orgEngagement ? '&OrgEngagement=true' : ''}${
          atpIlt ? '&AtpiLt=true' : ''
        }`,
      }),
    }),
    getAccountListV3: builder.query<
      AccountSelectQueryResult,
      AccountListApiRequest
    >({
      query: ({
        accountId = '',
        page = 1,
        pageSize = 10,
        search = '',
        sort = '',
        token = null,
        pointsOfContact = [],
        engagements = [],
      }: AccountListApiRequest) => {
        const body = {
          engagements,
          pointsOfContact,
        };
        if (body.pointsOfContact.length === 0) {
          delete body.pointsOfContact;
        }
        return {
          headers: {
            ...(token && { authorization: `Bearer ${token}` }),
          },
          method: 'POST',
          url: `v4/accounts/list?api-version=4.0&accountId=${accountId}&page=${page}&pageSize=${pageSize}&search=${search}&sort=${
            sort || ''
          }`,
          body,
        };
      },
    }),
    getAccountFavoriteList: builder.query<
      AccountSelectDto[],
      AccountListApiRequest
    >({
      query: ({ token = null }) => ({
        headers: {
          ...(token && { authorization: `Bearer ${token}` }),
        },
        url: `/userpreferences/favorites`,
      }),
    }),
    getAllPointsOfContacts: builder.query<Contact[], { token: string | null }>({
      query: ({ token = null }) => ({
        headers: {
          ...(token && { authorization: `Bearer ${token}` }),
        },
        url: `/v4/accounts/PointsOfContact`,
      }),
    }),
    addAccountFavorite: builder.mutation<void, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/userpreferences/favorites/${accountId}`,
        method: 'PUT',
      }),
    }),
    deleteAccountFavorite: builder.mutation<void, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/userpreferences/favorites/${accountId}`,
        method: 'DELETE',
      }),
    }),
    getEngagements: builder.query<string[], { token: string | null }>({
      query: ({ token = null }) => ({
        headers: {
          ...(token && { authorization: `Bearer ${token}` }),
        },
        url: `v4/accounts/Engagements`,
      }),
    }),
    getAdminInvitationList: builder.query<AdminInvite[], void>({
      query: () => `v4/accounts/invites/pending`,
    }),
  }),
});

export const {
  useGetAccountListQuery,
  useLazyGetAccountListQuery,
  useGetAccountFavoriteListQuery,
  useAddAccountFavoriteMutation,
  useDeleteAccountFavoriteMutation,
  useGetAccountListV3Query,
  useGetAllPointsOfContactsQuery,
  useGetEngagementsQuery,
  useGetAdminInvitationListQuery,
} = apiSlice;
